import { Component, computed, inject, signal, TemplateRef, viewChild } from '@angular/core'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { combineLatest, delay, firstValueFrom, from, switchMap, tap } from 'rxjs'
import { MDMABestAvailable, MDMAJobStatus, MDMAUnifiedStatus } from '../settlements/mdma-settlements.types'
import { Location } from '@angular/common'
import { Router, ActivatedRoute } from '@angular/router'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { MDMASettlementsService } from '../settlements/mdma-settlements.service'
import { DashboardService } from 'src/app/shared/services/dashboard.service'
import { dateOnlyFormat, dateTimeWithoutTimezone, est, formatAsUTC } from 'src/app/shared/utilities/date'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { PageContentComponent } from '../page-content/page-content.component'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { MDMAComparisonSelectionModalComponent } from '../modals/mdma-comparison-selection-modal/mdma-comparison-selection-modal.component'
import openModal from 'src/app/shared/services/openModal'
import { DataTableComponent } from '../table/data-table.component'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { TablePaginationComponent } from '../table/table-pagination/table-pagination.component'
import { ColDef, GetRowIdFunc, ICellRendererParams } from 'ag-grid-community'
import { PaginationParams } from 'src/app/shared/utilities/http-params'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { actionColumn } from '../table/cells/with-cell'
import { TableHeaderComponent } from '../table/headers/table-header.component'
import { DateCellComponent, DateCellComponentProps } from '../table/cells/date-cell.component'
import { EntityFilterModalComponent } from '../modals/filters/entity-filter-modal.component'
import { EntityCellComponent, EntityCellComponentProps } from '../table/cells/entity-cell.component'
import { formatDuration } from 'src/app/shared/utilities/formatDuration'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { ToastService } from '../toasts/toasts.service'
import { TableSortService } from '../table/services/table-sort.service'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'
import { DateDisplayComponent } from '../date-display/date-display.component'
import { LabeledDisplayComponent } from '../labeled-display/labeled-display.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { format } from 'date-fns'
import {
  CreateRunArgs,
  CreateRunModalState,
  CreateSettlementRunModalComponent,
} from '../modals/create-settlement-run-modal.component'
import { FilterMeta } from '../../shared/services/filter/common'
import { TBillJobStatus } from '../settlements/tbill-settlements.types'
import { StringFilterModalComponent } from '../modals/filters/string-filter-modal.component'
import { StringFilter } from '../../shared/services/filter/string-filter'
import { formatUser, getUserFilterConfig } from '../../shared/utilities/user'

const modalOptions: NgbModalOptions = { centered: true }

const filterGroup = 'MDMAAggregationsComponent'

const headers: Readonly<Partial<Record<keyof MDMAJobStatus, string>>> = {
  operating_date: 'Operating Day',
  settlement_type_code: 'Settlement Type',
  unified_status_code: 'Status',
  start_time: 'Start Time (STL)',
  end_time: 'End Time (STL)',
  duration: 'Duration',
  create_time: 'Create Time (STL)',
  created_by: 'Created By',
}

const getHeader = (field: keyof MDMAJobStatus): string =>
  headers[field] ?? field

const getDateCellParams = (
  field: keyof MDMAJobStatus,
  format: string,
  viewAsTZ: string,
): DateCellComponentProps<MDMAJobStatus> => ({
  date: (status: MDMAJobStatus) => status[field] as Date | null,
  format,
  viewAsTZ,
})

const dateColumn = (
  field: keyof MDMAJobStatus,
  dateFormat: string,
  viewAsTZ = 'America/Chicago',
): ColDef => ({
  headerName: headers[field],
  field,
  headerComponentParams: {
    filterGroup,
    modal: DateRangeFilterModalComponent,
    modalOptions,
  },
  cellRenderer: DateCellComponent,
  cellRendererParams: getDateCellParams(field, dateFormat, viewAsTZ),
})


type EntityCellProps = EntityCellComponentProps<MDMAJobStatus>

const getEntityCellParams = (field: keyof MDMAJobStatus): EntityCellProps => ({
  value: (status: MDMAJobStatus) => status[field] as ReturnType<EntityCellProps['value']>,
})

const entityColumn = (
  field: keyof MDMAJobStatus,
  valueField: keyof MDMAJobStatus = field,
  params: EntityCellProps = getEntityCellParams(valueField),

): ColDef => ({
  headerName: headers[field],
  field,
  headerComponentParams: {
    filterGroup,
    modal: EntityFilterModalComponent,
    modalOptions,
  },
  cellRenderer: EntityCellComponent,
  cellRendererParams: params,
})

const getMeta = (fieldName: keyof MDMAJobStatus): FilterMeta => ({
  fieldGroup: filterGroup,
  fieldName,
  fieldDisplay: getHeader(fieldName),
})

const getUnifiedStatus = ({ unified_status_code }: MDMAJobStatus): string => {
  if (unified_status_code.startsWith('LEGACY_')) {
    return `Legacy ${MDMAUnifiedStatus[unified_status_code.slice(7)] ?? 'Unknown'}`
  } else {
    return MDMAUnifiedStatus[unified_status_code] ?? 'Unknown'
  }
}

@Component({
  imports: [
    ActiveFilterListComponent,
    DataTableComponent,
    DateDisplayComponent,
    LabeledDisplayComponent,
    LabeledPropertyComponent,
    PageContentComponent,
    PageHeaderComponent,
    TablePaginationComponent,
  ],
  providers: [TableSortService],
  selector: 'app-mdma-aggregations',
  template: `
    <div app-page-header header="MDMA Settlement Runs" [menuItems]="pageHeaderMenuItems"></div>
    <app-page-content>
      <app-active-filter-list [filterGroup]="filterGroup"/>
      <app-data-table
          heightStyle="calc(100vh - 320px)"
          [loading]="loading()"
          [data]="data()"
          [getRowId]="getRowId"
          [defaultColDef]="defaultColDef"
          [columnDefs]="columnDefs"
      />
      <app-table-pagination
          [pageNumber]="pageNumber()"
          [pageSize]="pageSize()"
          [totalResults]="totalResults()"
          (pageNumberChange)="pageNumber.set($event)"
          (pageSizeChange)="pageSize.set($event)"
      />
    </app-page-content>

    <ng-template #publishConfirmation let-ctx="ctx">
      Are you sure you want to publish this MDMA aggregation run?
      @if (ctx) {
        <div app-labeled-property label="Operating Day">
          <div app-date-display [value]="ctx.operating_date" [format]="dateOnlyFormat"></div>
        </div>
        <div app-labeled-property label="Settlement Type">
          <div app-labeled-display [value]=" ctx.settlement_type_code"></div>
        </div>
        <div app-labeled-property label="Start Time (STL)">
          <div app-date-display [value]="ctx.start_time" [format]="dateTimeWithoutTimezone" viewAsTZ="America/Chicago"></div>
        </div>
      }
    </ng-template>

    <ng-template #unpublishConfirmation let-ctx="ctx">
      Are you sure you want to unpublish this MDMA aggregation run?
      @if (ctx) {
        <div app-labeled-property label="Operating Day">
          <div app-date-display [value]="ctx.operating_date" [format]="dateOnlyFormat"></div>
        </div>
        <div app-labeled-property label="Settlement Type">
          <div app-labeled-display [value]=" ctx.settlement_type_code"></div>
        </div>
        <div app-labeled-property label="Start Time (STL)">
          <div app-date-display [value]="ctx.start_time" [format]="dateTimeWithoutTimezone" viewAsTZ="America/Chicago"></div>
        </div>
      }
    </ng-template>
  `,
})
export class MDMAAggregationsComponent {
  private readonly modalService = inject(NgbModal)
  private readonly toastService = inject(ToastService)
  private readonly settlementsService = inject(MDMASettlementsService)
  private readonly dashboardService = inject(DashboardService)
  private readonly filterService = inject(FilterService)
  private readonly sortService = inject(TableSortService)
  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly location = inject(Location)

  protected readonly dateTimeWithoutTimezone = dateTimeWithoutTimezone
  protected readonly dateOnlyFormat = dateOnlyFormat
  protected readonly filterGroup = filterGroup

  protected readonly refresh = signal(0)
  protected readonly loading = signal(false)
  protected readonly pageSize = signal(25)
  protected readonly pageNumber = signal(1)
  protected readonly totalResults = signal(0)
  protected readonly data = signal<MDMAJobStatus[]>([])
  protected readonly getRowId: GetRowIdFunc<MDMAJobStatus> = (data) => data.data.run_instance_uid + data.data.unified_status_code
  protected readonly paginationParams = computed<PaginationParams>(() => ({
    pageNumber: this.pageNumber(),
    pageSize: this.pageSize(),
  }))

  private readonly publishConfirmationTemplate = viewChild<TemplateRef<{ ctx: MDMAJobStatus }>>('publishConfirmation')
  private readonly unpublishConfirmationTemplate = viewChild<TemplateRef<{ ctx: MDMAJobStatus }>>('unpublishConfirmation')

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Run ...', onClick: () => this.openCreateRunModal() },
  ]

  protected readonly defaultColDef: ColDef = {
    editable: false,
    sortable: true,
    filter: true,
    minWidth: 100,
    width: 175,
    headerComponent: TableHeaderComponent,
  }

  protected readonly columnDefs: ColDef[] = [
    dateColumn('operating_date', dateOnlyFormat, 'EST'),
    entityColumn('settlement_type_code'),
    entityColumn('unified_status_code', 'unified_status_code', { value: getUnifiedStatus }),
    dateColumn('start_time', dateTimeWithoutTimezone),
    dateColumn('end_time', dateTimeWithoutTimezone),
    {
      headerName: headers.duration,
      field: 'duration',
      cellRenderer: ({ value }: ICellRendererParams<TBillJobStatus, number>) => formatDuration(value!),
      filter: false,
      sortable: false,
    },
    {
      headerName: 'Created by',
      field: 'created_by',
      width: 160,
      cellRenderer: ({ value }: ICellRendererParams<TBillJobStatus, string>) => formatUser(value),
      headerComponentParams: {
        filterGroup,
        modal: StringFilterModalComponent,
        modalOptions,
      },
    },
    dateColumn('create_time', dateTimeWithoutTimezone),
    actionColumn<MDMAJobStatus>(filterGroup, {
      actions: [
        {
          name: 'Cancel',
          only: (d) =>
            ['SETTLEMENT_IN_PROGRESS'].includes(d.unified_status_code),
          onAction: () => alert('Cancel is not implemented yet'),
          disabled: () => true,
        },
        {
          name: 'Re-Run',
          only: (d) =>
            [
              'SETTLEMENT_FAILED',
              'GENERATING_REPORTS',
              'REPORT_GEN_FAILED',
              'SETTLEMENT_GENERATED',
              'LEGACY_SETTLEMENT_COMPLETE',
              'PUBLISHING',
              'PUBLISHING_FAILED',
              'PUBLISHED',
              'SUBMITTING',
              'SUBMISSION_FAILED',
              'SUBMITTED',
              'REJECTED',
              'CANCELLED',
            ].includes(d.unified_status_code),
          onAction: (d: MDMAJobStatus) => this.openRerunModal(d),
        },
        {
          name: 'Regenerate Report',
          only: (d: MDMAJobStatus) =>
            ['REPORT_GEN_FAILED'].includes(d.unified_status_code),
          onAction: () => alert('Regenerate Report is not implemented yet'),
          disabled: () => true,
        },
        {
          name: 'Reject',
          only: (d: MDMAJobStatus) =>
            ['REPORT_GEN_FAILED', 'SETTLEMENT_GENERATED'].includes(d.unified_status_code),
          onAction: () => alert('Reject is not implemented yet'),
          disabled: () => true,
        },
        {
          name: 'View Prepub Report',
          only: (d: MDMAJobStatus) =>
            [
              'SETTLEMENT_GENERATED',
              'LEGACY_SETTLEMENT_COMPLETE',
              'PUBLISHING',
              'PUBLISHING_FAILED',
              'PUBLISHED',
              'SUBMITTING',
              'SUBMISSION_FAILED',
              'SUBMITTED',
            ].includes(d.unified_status_code),
          onAction: (d: MDMAJobStatus) => this.goToPrepubDashboard(d),
        },
        {
          name: 'Comparison Report',
          only: (d: MDMAJobStatus) =>
            [
              'SETTLEMENT_GENERATED',
              'LEGACY_SETTLEMENT_COMPLETE',
              'PUBLISHING',
              'PUBLISHING_FAILED',
              'PUBLISHED',
              'SUBMITTING',
              'SUBMISSION_FAILED',
              'SUBMITTED',
            ].includes(d.unified_status_code),
          onAction: (d: MDMAJobStatus) => this.openMDMAComparisonModal(d),
        },
        {
          name: 'View LLS',
          only: (d: MDMAJobStatus) =>
            [
              'SETTLEMENT_GENERATED',
              'LEGACY_SETTLEMENT_COMPLETE',
              'PUBLISHING',
              'PUBLISHING_FAILED',
              'PUBLISHED',
              'SUBMITTING',
              'SUBMISSION_FAILED',
              'SUBMITTED',
            ].includes(d.unified_status_code),
          onAction: (d: MDMAJobStatus) => this.goToLLSDashboard(d),
        },
        {
          name: 'View Publish Detail',
          only: (d: MDMAJobStatus) =>
            [
              'PUBLISHED',
              'PUBLISHING_FAILED',
              'SUBMITTING',
              'SUBMISSION_FAILED',
              'SUBMITTED',
            ].includes(d.unified_status_code),
          onAction: (d: MDMAJobStatus) => this.goToPublishedDetailsReviewDashboard(d),
        },
        {
          name: 'View Submission Details',
          only: (d: MDMAJobStatus) => ['SUBMITTED'].includes(d.unified_status_code),
          onAction: () => alert('View Submission Details is not implemented yet'),
          disabled: () => true,
        },
        {
          name: 'Publish',
          only: (d: MDMAJobStatus) =>
            [
              'SETTLEMENT_GENERATED',
              'LEGACY_SETTLEMENT_COMPLETE',
              'PUBLISHING_FAILED',
            ].includes(d.unified_status_code) && !d.has_similar_published_runs,
          onAction: (d: MDMAJobStatus) => this.openPublishModal(d),
        },
        {
          name: 'Unpublish',
          only: (d: MDMAJobStatus) => ['PUBLISHED'].includes(d.unified_status_code),
          onAction: (d: MDMAJobStatus) => this.openUnpublishModal(d),
        },
        {
          name: 'Submit to MISO',
          only: (d: MDMAJobStatus) => ['PUBLISHED'].includes(d.unified_status_code),
          onAction: () => alert('Submit to MISO is not implemented yet'),
          disabled: () => true,
        },
      ],
    }),
  ]

  constructor() {
    const dispatch = this.filterService.dispatch.bind(this.filterService) as (a: unknown) => void
    this.filterService.dispatch(FilterService.InitFiltersAction({
      fieldGroup: filterGroup,
      fields: {
        settlement_type_code: new EntityFilter({
          config: {
            allowMultiple: true,
            resource: 'mdmaSettlementTypes',
            meta: getMeta('settlement_type_code'),
          },
        }, dispatch),
        unified_status_code: new EntityFilter({
          config: {
            allowMultiple: true,
            resource: 'mdmaUnifiedStatuses',
            meta: getMeta('unified_status_code'),
          },
        }, dispatch),
        operating_date: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            sendTime: false,
            meta: getMeta('operating_date'),
          },
        }, dispatch),
        start_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: getMeta('start_time'),
          },
        }, dispatch),
        end_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: getMeta('end_time'),
          },
        }, dispatch),
        created_by: new StringFilter(
          { config: getUserFilterConfig(filterGroup, 'created_by', getHeader('created_by')) },
          dispatch,
        ),
        create_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: getMeta('create_time'),
          },
        }, dispatch),
      },
    }))

    combineLatest([
      toObservable(this.paginationParams),
      this.filterService
        .asParams(filterGroup)
        .pipe(
          tap(() => {
            if (this.pageNumber() !== 1) {
              this.pageNumber.set(1)
            }
          }),
        ),
      this.sortService.getOrder(),
      toObservable(this.refresh).pipe(
        tap(() => this.loading.set(true)),
        delay(0), // increase if things are not showing correctly
      ),
    ])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loading.set(true)),
        switchMap(([pagination, filters, order]) => this.settlementsService.getMDMAJobs(pagination, filters, order)),
      )
      .subscribe({
        error: err => {
          this.toastService.showError(err)
        },
        next: (res) => {
          this.totalResults.set(res.total)
          this.data.set(res.results)
          this.loading.set(false)
        },
      })

    from(this.filterService.manageQueryParms(
      this.router,
      this.route,
      this.location,
      filterGroup,
    ))
      .pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  private onRun = ({ target, settlementType }: CreateRunArgs) => {
    this.settlementsService.runMDMA(target, settlementType)
      .subscribe({
        error: (e) => {
          this.toastService.showError(e)
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Run requested successfully' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })

  }

  private openRerunModal(data: MDMAJobStatus) {
    this.openCreateRunModal({
      operating_date: {
        value: data.operating_date,
        operation: 'eq',
      },
      settlement_type: {
        entity_code: data.settlement_type_code,
        entity_name: data.settlement_type_code,
      },
    })
  }

  private openCreateRunModal(modalState: CreateRunModalState = {}) {
    openModal(this.modalService, CreateSettlementRunModalComponent, { centered: true })
      .init('mdma', this.onRun, modalState)

  }

  private openPublishModal(data: MDMAJobStatus) {
    openModal(this.modalService, ConfirmationModalComponent<MDMAJobStatus>, { centered: true })
      .init({
        headerText: 'Confirm Publish',
        confirmButtonText: 'Publish',
        canConfirm: () => !data.has_similar_published_runs,
        onCancel: () => this.modalService.dismissAll(),
        onConfirm: () => this.publish(data, false),
        bodyContext: data,
        bodyTemplate: this.publishConfirmationTemplate(),
      })
  }

  private publish(data: MDMAJobStatus, unpublish_similar: boolean) {
    this.settlementsService
      .publishMDMA(data.run_instance_uid, unpublish_similar)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong publishing this run!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Publish requested successfully!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  private openUnpublishModal(data: MDMAJobStatus) {
    openModal(this.modalService, ConfirmationModalComponent<MDMAJobStatus>, { centered: true })
      .init({
        headerText: 'Confirm Unpublish',
        confirmButtonText: 'Unpublish',
        onCancel: () => this.modalService.dismissAll(),
        onConfirm: () => this.unpublish(data),
        bodyContext: data,
        bodyTemplate: this.unpublishConfirmationTemplate(),
      })
  }

  private unpublish(data: MDMAJobStatus) {
    this.settlementsService
      .unpublishMDMA(data.run_instance_uid)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong unpublishing this run!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Unpublish requested successfully!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  private openMDMAComparisonModal(data: MDMAJobStatus) {
    openModal(this.modalService, MDMAComparisonSelectionModalComponent, { centered: true })
      .init({
        selectedRun: data,
        onCancel: () => {
          this.modalService.dismissAll()
        },
        onSelect: (selected: MDMAJobStatus, comparison: MDMABestAvailable) => {
          this.modalService.dismissAll()
          void this.goToSettlementComparisonDashboard(selected, comparison)
        },
      })
  }

  private async goToSettlementComparisonDashboard(selected: MDMAJobStatus, comparison: MDMABestAvailable) {
    const dashboards = await firstValueFrom(this.dashboardService.get())
    const dashboardName = 'settlement_comparison_report_settlement_comparison_report'
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        DashboardService.getDashboardUrlParts(
          dashboards,
          dashboardName,
        ),
        {
          queryParams: {
            [DashboardService.getWidget(dashboards, dashboardName, 'run_id')]: selected.run_instance_uid,
            [DashboardService.getWidget(dashboards, dashboardName, 'settlement_type')]: selected.settlement_type_code,
            [DashboardService.getWidget(dashboards, dashboardName, 'operating_date')]: formatAsUTC(selected.operating_date, false),
            [DashboardService.getWidget(dashboards, dashboardName, 'comparison_run_id')]: comparison.run_instance_uid,
            [DashboardService.getWidget(dashboards, dashboardName, 'comparison_settlement_type')]: comparison.settlement_type,
            [DashboardService.getWidget(dashboards, dashboardName, 'comparison_operating_date')]: formatAsUTC(comparison.operating_day, false),
          },
        },
      ),
    )
    window.open(url, '_blank')
  }

  private async goToLLSDashboard(data: MDMAJobStatus) {
    const dashboards = await firstValueFrom(this.dashboardService.get())
    const dashboardName = 'lowest_level_summary_lowest_level_summary'
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        DashboardService.getDashboardUrlParts(dashboards, dashboardName),
        {
          queryParams: {
            [DashboardService.getWidget(dashboards, dashboardName, 'run-instance-uids')]: data.run_instance_uid,
            [DashboardService.getWidget(dashboards, dashboardName, 'operating-dates')]: `gt${data.operating_date}`,
          },
        },
      ),
    )
    window.open(url, '_blank')
  }


  private async goToPrepubDashboard(data: MDMAJobStatus) {
    const dashboards = await firstValueFrom(this.dashboardService.get())
    const previous = await firstValueFrom(
      this.settlementsService.getPreviousRunMDMA(data.run_instance_uid),
    )
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        DashboardService.getDashboardUrlParts(
          dashboards,
          'mdma_prepub_review_mdma_prepub_review',
        ),
        {
          queryParams: {
            [DashboardService.getWidget(
              dashboards,
              'mdma_prepub_review_mdma_prepub_review',
              'operating_date',
            )]: format(est(data.operating_date), dateOnlyFormat),
            [DashboardService.getWidget(
              dashboards,
              'mdma_prepub_review_mdma_prepub_review',
              'settlement_type',
            )]: data.settlement_type_code,
            [DashboardService.getWidget(
              dashboards,
              'mdma_prepub_review_mdma_prepub_review',
              'primary_run_id',
            )]: data.run_instance_uid,
            ...(previous?.prev_operating_date_run_instance_uid
              ? {
                [DashboardService.getWidget(
                  dashboards,
                  'mdma_prepub_review_mdma_prepub_review',
                  'prev_date_run_id',
                )]: previous.prev_operating_date_run_instance_uid,
              }
              : {}),
            ...(previous?.prev_settlement_type_run_instance_uid
              ? {
                [DashboardService.getWidget(
                  dashboards,
                  'mdma_prepub_review_mdma_prepub_review',
                  'prev_type_run_id',
                )]: previous.prev_settlement_type_run_instance_uid,
              }
              : {}),
          },
        },
      ),
    )
    window.open(url, '_blank')
  }

  private async goToPublishedDetailsReviewDashboard(data: MDMAJobStatus) {
    const dashboards = await firstValueFrom(this.dashboardService.get())
    const previous = await firstValueFrom(
      this.settlementsService.getPreviousRunMDMA(data.run_instance_uid),
    )
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        DashboardService.getDashboardUrlParts(
          dashboards,
          'published_details_review_published_details_review',
        ),
        {
          queryParams: {
            [DashboardService.getWidget(
              dashboards,
              'published_details_review_published_details_review',
              'operating_date',
            )]: format(est(data.operating_date), dateOnlyFormat),
            [DashboardService.getWidget(
              dashboards,
              'published_details_review_published_details_review',
              'settlement_type',
            )]: data.settlement_type_code,
            [DashboardService.getWidget(
              dashboards,
              'published_details_review_published_details_review',
              'primary_run_id',
            )]: data.run_instance_uid,
            ...(previous?.prev_operating_date_run_instance_uid
              ? {
                [DashboardService.getWidget(
                  dashboards,
                  'published_details_review_published_details_review',
                  'prev_day_run_id',
                )]: previous.prev_operating_date_run_instance_uid,
              }
              : {}),
            ...(previous?.prev_settlement_type_run_instance_uid
              ? {
                [DashboardService.getWidget(
                  dashboards,
                  'published_details_review_published_details_review',
                  'prev_type_run_id',
                )]: previous.prev_settlement_type_run_instance_uid,
              }
              : {}),
          },
        },
      ),
    )
    window.open(url, '_blank')
  }
}
